import {
  Button,
  message,
  Tabs,
  Spin,
  Tag,
  Modal,
  Collapse,
  Dropdown,
  Card,
  Descriptions,
  Table,
  List,
  Space,
  Typography,
  Row,
  Col,
} from "antd"
import type { MenuProps } from "antd"
import { DownOutlined } from "@ant-design/icons"
import type React from "react"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import { headwordService } from "@/services/dictionary/headwordApi"
import { getCefrColor } from "@/utils/utils"

const { Text } = Typography

// ─── GeneralInfo ────────────────────────────────────────────────────────────────
const GeneralInfo = ({ data }: { data: any }) => {
  return (
    <Descriptions
      bordered
      size="small"
      column={{ xs: 1, sm: 2 }}
      className="mt-4"
      items={[
        {
          key: "word",
          label: (
            <Text type="secondary" className="font-semibold">
              Từ vựng
            </Text>
          ),
          children: <span className="font-extrabold text-xl">{data?.word}</span>,
        },
        {
          key: "pronunciation",
          label: (
            <Text type="secondary" className="font-semibold">
              Phiên âm
            </Text>
          ),
          children: (
            <Space size="middle" wrap>
              {data?.pronunciation?.us && (
                <Space>
                  <Tag color="cyan" className="font-bold">
                    US
                  </Tag>
                  <span className="font-mono text-primary">{data.pronunciation.us}</span>
                </Space>
              )}
              {data?.pronunciation?.uk && (
                <Space>
                  <Tag color="blue" className="font-bold">
                    UK
                  </Tag>
                  <span className="font-mono text-primary">{data.pronunciation.uk}</span>
                </Space>
              )}
              {!data?.pronunciation?.us && !data?.pronunciation?.uk && <Text type="secondary">N/A</Text>}
            </Space>
          ),
        },
      ]}
    />
  )
}

// ─── SenseItem ──────────────────────────────────────────────────────────────────
const SenseItem = ({ sense, index }: { sense: any; index: number }) => {
  return (
    <Card
      title={
        <Space wrap>
          <span className="font-bold text-primary"># Nghĩa {index + 1}</span>
          {sense.part_of_speech && <Tag color="blue">{sense.part_of_speech}</Tag>}
          {sense.cefr_level && <Tag color={getCefrColor(sense.cefr_level)}>{sense.cefr_level}</Tag>}
        </Space>
      }
      className="mt-4"
      size="small"
    >
      <Space size="large" wrap className="mb-4 text-sm">
        {sense.topics?.length > 0 && (
          <div>
            <Text type="secondary" className="mr-2 font-medium">
              Chủ đề:
            </Text>
            <Space wrap>
              {sense.topics.map((t: any) => (
                <Tag key={t.id} color="volcano">
                  {t.name}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {sense.grammar_labels?.length > 0 && (
          <div>
            <Text type="secondary" className="mr-2 font-medium">
              Nhãn ngữ pháp:
            </Text>
            <Space wrap>
              {sense.grammar_labels.map((label: string) => (
                <Tag key={label} color="green">
                  {label}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </Space>

      <Descriptions
        bordered
        column={1}
        size="small"
        className="mb-4"
        items={[
          {
            key: "definition",
            label: (
              <Text type="secondary" className="font-semibold">
                Nghĩa tiếng Anh
              </Text>
            ),
            children: <div className="font-medium">{sense.definition || "N/A"}</div>,
          },
          {
            key: "meaning",
            label: (
              <Text type="secondary" className="font-semibold">
                Nghĩa tiếng Việt
              </Text>
            ),
            children: <div className="font-bold text-primary">{sense.meaning || "N/A"}</div>,
          },
        ]}
      />

      {((sense.examples && sense.examples.length > 0) || (sense.collocations && sense.collocations.length > 0)) && (
        <div className="mt-4 flex flex-col gap-3">
          {sense.examples?.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={sense.examples}
              renderItem={(ex: any) => (
                <List.Item className="px-3">
                  <Space direction="vertical" size={0}>
                    <Text>{ex.sentence}</Text>
                    {ex.meaning && <Text type="secondary">({ex.meaning})</Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}

          {sense.collocations?.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={sense.collocations}
              renderItem={(col: any) => {
                const firstExample = col.examples?.[0]
                return (
                  <List.Item className="px-3">
                    <Space direction="vertical" size={0} className="w-full">
                      <div>
                        <Text strong className="text-primary">
                          {col.phrase}
                        </Text>
                        {firstExample && (
                          <>
                            <Text type="secondary"> - </Text>
                            <Text>{firstExample.sentence}</Text>
                          </>
                        )}
                      </div>
                      {firstExample?.meaning && <Text type="secondary">({firstExample.meaning})</Text>}
                    </Space>
                  </List.Item>
                )
              }}
            />
          )}
        </div>
      )}

      {sense.thesauruses?.length > 0 && (
        <Row gutter={[16, 16]} className="mt-4 border-slate-100 border-t pt-3">
          <Col xs={24} sm={12}>
            <div className="mb-2 font-semibold text-xs uppercase tracking-wider">
              <Text type="secondary">Đồng nghĩa</Text>
            </div>
            <Space wrap>
              {sense.thesauruses
                ?.filter((t: any) => t.relation_type === "synonym")
                .map((t: any) => (
                  <Tag key={t.id} color="blue">
                    {t.target_text}
                  </Tag>
                ))}
              {sense.thesauruses?.filter((t: any) => t.relation_type === "synonym").length === 0 && (
                <Text type="secondary">N/A</Text>
              )}
            </Space>
          </Col>
          <Col xs={24} sm={12}>
            <div className="mb-2 font-semibold text-xs uppercase tracking-wider">
              <Text type="secondary">Trái nghĩa</Text>
            </div>
            <Space wrap>
              {sense.thesauruses
                ?.filter((t: any) => t.relation_type === "antonym")
                .map((t: any) => (
                  <Tag key={t.id} color="red">
                    {t.target_text}
                  </Tag>
                ))}
              {sense.thesauruses?.filter((t: any) => t.relation_type === "antonym").length === 0 && (
                <Text type="secondary">N/A</Text>
              )}
            </Space>
          </Col>
        </Row>
      )}
    </Card>
  )
}

// ─── Senses ─────────────────────────────────────────────────────────────────────
const Senses = ({ data }: { data: any[] }) => {
  if (!data || data.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      <h3 className="inline-block w-fit border-primary border-b pb-2 font-bold text-lg text-primary">Nghĩa của từ</h3>
      {data.map((sense, index) => (
        <SenseItem key={sense.id} sense={sense} index={index} />
      ))}
    </div>
  )
}

// ─── WordForms ──────────────────────────────────────────────────────────────────
const mapFormType = (type: string) => {
  switch (type) {
    case "third_person_singular":
      return "Ngôi thứ 3 số ít (V-s/-es)"
    case "past_simple":
      return "Quá khứ đơn (V-ed)"
    case "past_participle":
      return "Quá khứ phân từ (VpII)"
    case "present_participle":
      return "Danh động từ (V-ing)"
    case "singular":
      return "Số ít"
    case "plural":
      return "Số nhiều"
    case "comparative":
      return "So sánh hơn"
    case "superlative":
      return "So sánh nhất"
    default:
      return type
  }
}

const WordForms = ({ forms, families }: { forms: any[]; families: any[] }) => {
  return (
    <>
      {forms.length > 0 && (
        <div className="mb-8">
          <h3 className="inline-block border-primary border-b pb-2 font-bold text-lg text-primary">Word form</h3>
          <Descriptions
            bordered
            size="small"
            className="mt-4"
            column={{ xs: 1, sm: 2 }}
            items={forms.map((form) => ({
              key: form.id,
              label: (
                <Text type="secondary" className="font-medium">
                  {mapFormType(form.form_type)}
                </Text>
              ),
              children: <span className="font-semibold text-primary">{form.head_word_text}</span>,
            }))}
          />
        </div>
      )}

      {families.length > 0 && (
        <div>
          <h3 className="inline-block border-primary border-b pb-2 font-bold text-lg text-primary">Word family</h3>
          <Table
            size="small"
            pagination={false}
            bordered
            className="mt-4"
            dataSource={families}
            rowKey="id"
            columns={[
              {
                title: (
                  <Text type="secondary" className="font-semibold">
                    Từ vựng
                  </Text>
                ),
                dataIndex: "family_word_text",
                key: "family_word_text",
                render: (text) => <span className="font-semibold">{text}</span>,
              },
              {
                title: (
                  <Text type="secondary" className="font-semibold">
                    Từ loại
                  </Text>
                ),
                dataIndex: "part_of_speech",
                key: "part_of_speech",
                render: (pos) => <Tag color="blue">{pos || "N/A"}</Tag>,
              },
            ]}
          />
        </div>
      )}
    </>
  )
}

// ─── Idioms ─────────────────────────────────────────────────────────────────────
const Idioms = ({ idioms }: { idioms: any[] }) => {
  return (
    <div className="mt-4">
      {idioms.map((idiom, index) => (
        <Card
          key={idiom.id}
          title={
            <Space wrap>
              <span className="font-bold text-primary"># Idiom {index + 1}</span>
              <span className="font-bold text-lg">{idiom.phrase}</span>
            </Space>
          }
          className="mb-4"
          size="small"
        >
          <Descriptions
            bordered
            column={1}
            size="small"
            className="mb-3"
            items={[
              {
                key: "definition",
                label: (
                  <Text type="secondary" className="font-semibold">
                    Nghĩa tiếng Anh
                  </Text>
                ),
                children: <span className="text-slate-800">{idiom.definition || "N/A"}</span>,
              },
              {
                key: "meaning",
                label: (
                  <Text type="secondary" className="font-semibold">
                    Nghĩa tiếng Việt
                  </Text>
                ),
                children: <span className="font-bold text-primary">{idiom.meaning || "N/A"}</span>,
              },
            ]}
          />

          {idiom.examples?.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={idiom.examples}
              renderItem={(ex: any) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text italic>{ex.sentence}</Text>
                    {ex.meaning && <Text type="secondary">({ex.meaning})</Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Card>
      ))}

      {idioms.length === 0 && (
        <Card className="mt-4 text-center" size="small">
          <Text type="secondary">Chưa có dữ liệu cho Idioms</Text>
        </Card>
      )}
    </div>
  )
}

// ─── PhrasalVerbs ───────────────────────────────────────────────────────────────
const PhrasalVerbs = ({ phrasalVerbs }: { phrasalVerbs: any[] }) => {
  return (
    <div className="mt-4">
      {phrasalVerbs.map((pv, index) => (
        <Card
          key={pv.id}
          title={
            <Space wrap>
              <span className="font-bold text-primary"># Phrasal verb {index + 1}</span>
              <span className="font-bold text-lg">{pv.phrase}</span>
            </Space>
          }
          className="mb-4"
          size="small"
        >
          {pv.senses?.map((sense: any, sIdx: number) => (
            <div key={sense.id} className="mb-4 border-slate-100 border-b pb-4 last:mb-0 last:border-b-0 last:pb-0">
              <div className="mb-2 font-semibold text-sm">
                <Text type="secondary">Nghĩa {sIdx + 1}</Text>
              </div>
              <Descriptions
                bordered
                column={1}
                size="small"
                className="mb-3"
                items={[
                  {
                    key: "definition",
                    label: (
                      <Text type="secondary" className="font-semibold">
                        Nghĩa tiếng Anh
                      </Text>
                    ),
                    children: <span className="text-slate-800">{sense.definition || "N/A"}</span>,
                  },
                  {
                    key: "meaning",
                    label: (
                      <Text type="secondary" className="font-semibold">
                        Nghĩa tiếng Việt
                      </Text>
                    ),
                    children: <span className="font-bold text-primary">{sense.meaning || "N/A"}</span>,
                  },
                ]}
              />
              {sense.synonymous_words?.length > 0 && (
                <div className="mb-2">
                  <span className="mr-2 font-semibold text-xs uppercase tracking-wider">
                    <Text type="secondary">Từ đồng nghĩa:</Text>
                  </span>
                  <Space wrap>
                    {sense.synonymous_words.map((word: string, i: number) => (
                      <Tag key={i} color="blue">
                        {word}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          ))}

          {pv.examples?.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={pv.examples}
              renderItem={(ex: any) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text italic>{ex.sentence}</Text>
                    {ex.meaning && <Text type="secondary">({ex.meaning})</Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Card>
      ))}

      {phrasalVerbs.length === 0 && (
        <Card className="mt-4 text-center" size="small">
          <Text type="secondary">Chưa có dữ liệu cho Phrasal verbs</Text>
        </Card>
      )}
    </div>
  )
}

// ─── RelatedStructures ──────────────────────────────────────────────────────────
const RelatedStructures = ({ structures }: { structures: any[] }) => {
  return (
    <div className="flex flex-col gap-2">
      {structures.map((structure, index) => (
        <Card
          key={structure.id}
          title={
            <Space wrap>
              <span className="font-bold text-primary"># Cấu trúc {index + 1}</span>
              <span className="font-bold font-mono text-lg">{structure.structure}</span>
            </Space>
          }
          size="small"
        >
          <Descriptions
            bordered
            column={1}
            size="small"
            className="mb-3"
            items={[
              {
                key: "meaning",
                label: (
                  <Text type="secondary" className="font-semibold">
                    Nghĩa tiếng Việt
                  </Text>
                ),
                children: <span className="font-semibold">{structure.meaning || "N/A"}</span>,
              },
              ...(structure.note?.en?.text
                ? [
                    {
                      key: "note_en",
                      label: (
                        <Text type="secondary" className="font-semibold">
                          Lưu ý (EN)
                        </Text>
                      ),
                      children: <span className="text-slate-800">{structure.note.en.text}</span>,
                    },
                    {
                      key: "note_vi",
                      label: (
                        <Text type="secondary" className="font-semibold">
                          Lưu ý (VI)
                        </Text>
                      ),
                      children: <span className="text-slate-500 italic">{structure.note.en.translation || "N/A"}</span>,
                    },
                  ]
                : []),
            ]}
          />

          {structure.examples?.length > 0 && (
            <List
              size="small"
              bordered
              dataSource={structure.examples}
              renderItem={(ex: any) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text italic>{ex.sentence}</Text>
                    {ex.meaning && <Text type="secondary">({ex.meaning})</Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Card>
      ))}

      {structures.length === 0 && (
        <Card className="mt-4 text-center" size="small">
          <Text type="secondary">Chưa có dữ liệu cho Cấu trúc câu</Text>
        </Card>
      )}
    </div>
  )
}

// ─── PreviewModal ───────────────────────────────────────────────────────────────
const PreviewModal = ({ data, open, onClose }: { data: any; open: boolean; onClose: () => void }) => {
  if (!data) return null

  const commonSenses = data.senses?.filter((s: any) => s.is_common) || []
  const otherSenses = data.senses?.filter((s: any) => !s.is_common) || []

  const renderSense = (sense: any, index: number) => {
    return (
      <div key={sense.id} className="relative mb-4 rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <span className="font-extrabold text-xl">{index + 1}.</span>
          {sense.part_of_speech && (
            <span className="rounded-lg bg-[#e6f0fa] px-3 py-1 font-bold text-primary text-sm tracking-wide">
              {sense.part_of_speech}
            </span>
          )}
          {sense.cefr_level && (
            <span className="rounded-lg bg-[#e6f0fa] px-3 py-1 font-bold text-primary text-sm tracking-wide">
              {sense.cefr_level}
            </span>
          )}
          {sense.topics?.length > 0 && (
            <span className="text-base italic">[{sense.topics.map((t: any) => t.name).join(", ")}]</span>
          )}
          {sense.grammar_labels?.length > 0 && (
            <span className="text-base">
              <Text type="secondary">[{sense.grammar_labels.join(", ")}]</Text>
            </span>
          )}
        </div>

        <div className="mb-2 text-base">
          <Text strong>{sense.meaning}</Text>
        </div>
        <div className="mb-2 text-base">
          <Text type="secondary">{sense.definition}</Text>
        </div>

        {sense.examples?.map((ex: any, i: number) => (
          <div key={i} className="mb-2">
            <div className="border-primary border-l-2 pl-2 italic">
              <Text>{ex.sentence}</Text>
            </div>
            <div className="mt-1 pl-2">
              <Text type="secondary" className="text-sm">
                ({ex.meaning})
              </Text>
            </div>
          </div>
        ))}

        {sense.collocations?.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 text-xs uppercase">Cụm từ liên quan & Ví dụ</div>
            {sense.collocations.map((col: any) => (
              <div key={col.id} className="mb-3 pl-3">
                <div className="mb-1 flex items-center gap-2 font-bold text-sm">
                  <span className="inline-block h-1 w-1 rounded-full bg-(--ant-color-text-description)"></span>
                  <Text strong>{col.phrase}</Text>
                </div>
                {col.examples?.map((ex: any, i: number) => (
                  <div key={i} className="mt-1 ml-1 border-primary border-l-2 pl-2">
                    <div className="text-sm italic">
                      <Text>{ex.sentence}</Text>
                    </div>
                    <div>
                      <Text type="secondary" className="text-xs">
                        ({ex.meaning})
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {sense.thesauruses && sense.thesauruses.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-8 border-gray-50 border-t pt-4">
            {sense.thesauruses.some((t: any) => t.relation_type === "synonym") && (
              <div>
                <div className="mb-2 font-bold text-[10px] uppercase tracking-wider">
                  <Text type="secondary">Đồng nghĩa</Text>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sense.thesauruses
                    .filter((t: any) => t.relation_type === "synonym")
                    .map((t: any) => (
                      <span key={t.id} className="rounded bg-blue-50 px-2 py-0.5 font-medium text-blue-500 text-xs">
                        {t.target_text}
                      </span>
                    ))}
                </div>
              </div>
            )}
            {sense.thesauruses.some((t: any) => t.relation_type === "antonym") && (
              <div>
                <div className="mb-2 font-bold text-[10px] uppercase tracking-wider">
                  <Text type="secondary">Trái nghĩa</Text>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sense.thesauruses
                    .filter((t: any) => t.relation_type === "antonym")
                    .map((t: any) => (
                      <span key={t.id} className="rounded bg-red-50 px-2 py-0.5 font-medium text-red-500 text-xs">
                        {t.target_text}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      className="[&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:rounded-xl [&_.ant-modal-content]:bg-(--ant-color-bg-layout) [&_.ant-modal-content]:p-0"
      closeIcon={
        <Text type="secondary" className="font-bold text-xl">
          &times;
        </Text>
      }
    >
      <div className="max-h-[85vh] overflow-y-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 rounded-xl border border-(--ant-color-border-secondary) bg-(--ant-color-bg-container) p-6 shadow-sm">
          <div className="mb-4 flex items-end gap-3">
            <h1 className="m-0 font-extrabold text-4xl leading-none">{data.word}</h1>
            <span className="mb-1 font-bold text-sm uppercase leading-none tracking-widest">
              <Text type="secondary">{data.senses?.[0]?.part_of_speech}</Text>
            </span>
          </div>
          <div className="flex flex-wrap gap-8 text-sm">
            {data.pronunciation?.us && (
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs uppercase">
                  <Text type="secondary">US</Text>
                </span>
                <span className="font-medium text-base text-primary">{data.pronunciation.us}</span>
              </div>
            )}
            {data.pronunciation?.uk && (
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs uppercase">
                  <Text type="secondary">UK</Text>
                </span>
                <span className="font-medium text-base text-primary">{data.pronunciation.uk}</span>
              </div>
            )}
          </div>
        </div>

        {/* Common Senses */}
        {commonSenses.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Nghĩa thông dụng</Text>
            </div>
            {commonSenses.map((sense: any, index: number) => renderSense(sense, index))}
          </div>
        )}

        {/* Other Senses */}
        {otherSenses.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Các nghĩa khác</Text>
            </div>
            {otherSenses.map((sense: any, index: number) => renderSense(sense, commonSenses.length + index))}
          </div>
        )}

        {/* Word Forms */}
        {data.word_forms && data.word_forms.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Dạng từ</Text>
            </div>
            <div className="overflow-hidden rounded-lg border border-(--ant-color-border-secondary) bg-(--ant-color-bg-container) shadow-sm">
              <div className="grid grid-cols-12 bg-primary px-5 py-3 font-bold text-white text-xs uppercase tracking-wider">
                <div className="col-span-5">Dạng từ</div>
                <div className="col-span-7">Cách chia</div>
              </div>
              {data.word_forms.map((form: any) => (
                <div
                  key={form.id}
                  className="grid grid-cols-12 items-start border-(--ant-color-border-secondary) border-t px-5 py-4"
                >
                  <div className="col-span-5 font-bold text-primary text-xs">{mapFormType(form.form_type)}</div>
                  <div className="col-span-7">
                    <div className="mb-1 font-bold text-sm">
                      <Text>{form.head_word_text}</Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Word Families */}
        {data.word_families && data.word_families.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Họ từ</Text>
            </div>
            <div className="flex flex-wrap gap-4">
              {data.word_families.map((fam: any) => (
                <div
                  key={fam.id}
                  className="flex min-w-[120px] flex-col items-center justify-center rounded-lg border border-(--ant-color-border-secondary) bg-(--ant-color-bg-container) px-8 py-4 shadow-sm"
                >
                  <span className="mb-1 font-extrabold text-[10px] text-primary uppercase tracking-wider">
                    {fam.part_of_speech}
                  </span>
                  <span className="font-bold text-base">
                    <Text>{fam.family_word_text}</Text>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phrasal Verbs */}
        {data.phrasal_verbs && data.phrasal_verbs.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Cụm động từ</Text>
            </div>
            <div className="rounded-lg border border-(--ant-color-border-secondary) bg-(--ant-color-bg-container) shadow-sm">
              <Collapse
                ghost
                expandIconPlacement="end"
                className="[&_.ant-collapse-header]:py-4 [&_.ant-collapse-header]:font-bold [&_.ant-collapse-header]:text-inherit [&_.ant-collapse-item]:border-(--ant-color-border-secondary) [&_.ant-collapse-item]:border-b"
                items={data.phrasal_verbs.map((pv: any) => ({
                  key: pv.id,
                  label: pv.phrase,
                  children: (
                    <div className="mb-2 border-primary border-l-2 pl-2">
                      {pv.senses?.map((sense: any) => (
                        <div key={sense.id} className="mb-4 last:mb-0">
                          <div className="mb-1">
                            <Text strong>{sense.meaning}</Text>
                          </div>
                          <div className="mb-2 text-sm">
                            <Text type="secondary">{sense.definition}</Text>
                          </div>
                          {sense.synonymous_words?.length > 0 && (
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="font-bold text-xs uppercase">
                                <Text type="secondary">Đồng nghĩa:</Text>
                              </span>
                              {sense.synonymous_words.map((w: string, wi: number) => (
                                <span key={wi} className="rounded bg-(--ant-color-fill-secondary) px-2 py-0.5 text-xs">
                                  <Text type="secondary">{w}</Text>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ),
                }))}
              />
            </div>
          </div>
        )}

        {/* Idioms */}
        {data.idioms && data.idioms.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Thành ngữ</Text>
            </div>
            <div className="rounded-lg border border-(--ant-color-info-border) bg-(--ant-color-info-bg) p-5">
              {data.idioms.map((idiom: any) => (
                <div key={idiom.id} className="mb-5 last:mb-0">
                  <div className="mb-1 font-bold text-primary leading-snug">
                    {idiom.phrase}{" "}
                    <span className="ml-1 font-normal">
                      <Text type="secondary">- {idiom.meaning}</Text>
                    </span>
                  </div>
                  {idiom.examples?.map((ex: any, i: number) => (
                    <div key={i} className="mt-2 border-primary border-l-2 pl-2 text-sm">
                      <div className="italic">
                        <Text>{ex.sentence}</Text>
                      </div>
                      <div className="mt-0.5">
                        <Text type="secondary" className="text-xs">
                          ({ex.meaning})
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related Structures */}
        {data.related_structures && data.related_structures.length > 0 && (
          <div className="mb-6">
            <div className="mb-3 font-bold text-xs uppercase tracking-wider">
              <Text type="secondary">Cấu trúc câu</Text>
            </div>
            <div className="rounded-lg border border-(--ant-color-border-secondary) bg-(--ant-color-fill-alter) p-5">
              {data.related_structures.map((structure: any) => (
                <div key={structure.id} className="mb-5 last:mb-0">
                  <div className="mb-1 font-bold text-primary leading-snug">
                    {structure.structure}{" "}
                    <span className="ml-1 font-normal">
                      <Text type="secondary">- {structure.meaning}</Text>
                    </span>
                  </div>
                  {structure.note?.en?.text && (
                    <div className="mt-2 rounded border border-(--ant-color-border-secondary) bg-(--ant-color-bg-container) p-3 text-sm">
                      <div className="mb-1 font-semibold">
                        <Text type="secondary">Lưu ý:</Text>
                      </div>
                      <div className="mb-1">
                        <Text>{structure.note.en.text}</Text>
                      </div>
                      {structure.note.en.translation && (
                        <div className="mt-0.5">
                          <Text type="secondary" italic>
                            {structure.note.en.translation}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                  {structure.examples?.map((ex: any, i: number) => (
                    <div key={i} className="mt-2 border-slate-300 border-l-2 pl-2 text-sm">
                      <div className="italic">
                        <Text>{ex.sentence}</Text>
                      </div>
                      <div className="mt-0.5">
                        <Text type="secondary" className="text-xs">
                          ({ex.meaning})
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Detail (Main Container) ────────────────────────────────────────────────────
export const HeadwordDetailPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string>("1")
  const [previewVisible, setPreviewVisible] = useState<boolean>(false)
  const [previewData, setPreviewData] = useState<any>(null)

  // Fetch headword detail
  const { data: detailRes, isLoading: detailLoading } = useQuery({
    queryKey: ["headword_detail", id],
    queryFn: () => headwordService.detail(Number(id)),
    enabled: !!id,
  })

  // Fetch histories list
  const { data: historyRes } = useQuery({
    queryKey: ["headword_history", id],
    queryFn: () => headwordService.getHistories(Number(id)),
    enabled: !!id,
  })

  // Mutation to fetch history file content for preview modal
  const fetchHistoryDetailMutation = useMutation({
    mutationFn: (filename: string) => headwordService.getHistoryDetail(Number(id), filename),
    onSuccess: (res) => {
      if (res?.code === 200) {
        setPreviewData(res.data)
        setPreviewVisible(true)
      } else {
        message.error(res?.message || "Lỗi khi lấy thông tin lịch sử")
      }
    },
    onError: () => {
      message.error("Lỗi khi lấy thông tin lịch sử")
    },
  })

  const headword = detailRes?.data
  const histories = historyRes?.data || []

  const handlePreviewHistory: MenuProps["onClick"] = (e) => {
    fetchHistoryDetailMutation.mutate(e.key)
  }

  const historyMenuItems: MenuProps["items"] = histories.map((h: any) => {
    const title = typeof h === "string" ? h : h.filename || h.name || h
    return {
      key: typeof h === "string" ? h : h.filename || h.key || h,
      label: title,
    }
  })

  console.log(headword)

  const items = [
    {
      key: "1",
      label: "Thông tin cơ bản",
      children: <Senses data={headword?.senses || []} />,
    },
    {
      key: "3",
      label: "Dạng thức từ",
      children: <WordForms forms={headword?.word_forms || []} families={headword?.word_families || []} />,
    },
    {
      key: "4",
      label: "Cụm động từ",
      children: <PhrasalVerbs phrasalVerbs={headword?.phrasal_verbs || []} />,
    },
    {
      key: "6",
      label: "Thành Ngữ",
      children: <Idioms idioms={headword?.idioms || []} />,
    },
    {
      key: "7",
      label: "Cấu trúc câu",
      children: <RelatedStructures structures={headword?.related_structures || []} />,
    },
  ]

  const loading = detailLoading || fetchHistoryDetailMutation.isPending

  return (
    <div className="flex flex-col gap-2">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .bg-primary {
          background-color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>
      {loading ? (
        <div className="flex justify-center p-10">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <GeneralInfo data={headword} />
          <Tabs
            items={items}
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            tabBarExtraContent={
              <div className="flex gap-2">
                <Dropdown
                  menu={{
                    items: historyMenuItems,
                    onClick: handlePreviewHistory,
                  }}
                  disabled={!headword || historyMenuItems.length === 0}
                >
                  <Button>
                    Xem lịch sử <DownOutlined />
                  </Button>
                </Dropdown>
                <Button onClick={() => navigate(-1)}>Quay lại</Button>
              </div>
            }
          />
        </>
      )}

      {previewVisible && previewData && (
        <PreviewModal
          data={previewData}
          open={previewVisible}
          onClose={() => {
            setPreviewVisible(false)
            setPreviewData(null)
          }}
        />
      )}
    </div>
  )
}

export default HeadwordDetailPage
